import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { useToast } from '@kanda-libs/ks-design-library';
import { Company, UserType, actions } from '@kanda-libs/ks-frontend-services';

import { APP_ENV } from 'common/constants';
import { useAppDispatch } from 'components/shared/App';
import useReloadInitialData from 'hooks/useReloadInitialData';
import companySelectors from 'store/selectors/company';

import type { FormattedDirector } from '../../helpers';

export interface DirectorPropsHook {
  onClick: (email: string) => void;
  isSubmitting: boolean;
  generateUrl: (director: FormattedDirector) => string;
  editingIndex: number | null;
  setEditingIndex: (index: number | null) => void;
  onEditDirector: (index: number, director: UserType) => void;
}

export default function useDirectorProps(): DirectorPropsHook {
  const dispatch = useAppDispatch();
  const company = useSelector(companySelectors.getEntity);
  const isSubmitting = useSelector(companySelectors.getIsSubmitting);
  const refreshAll = useReloadInitialData();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const { showSuccess, showError } = useToast();

  const onClick = useCallback(
    (email: string) => {
      const index = company?.users?.findIndex(
        (user: UserType) => user?.email === email,
      );
      if (index == null) {
        showError("Issue updating user - can't find index");
        return;
      }
      const users = company?.users?.map((user: UserType, userIndex: number) => {
        if (userIndex !== index) return user;
        return {
          ...user,
          director_info: {
            ...user?.director_info,
            verification_status: 'verified',
          },
        };
      });
      const body = {
        ...company,
        users,
      };
      const id = company?.id;

      dispatch(
        actions.putCompany({
          body: body as Company,
          params: { id: id as string },
          onSuccess: () => {
            showSuccess("User's verification status updated");
            refreshAll();
          },
          onError: () => {
            showError("Error updating user's verification status");
          },
        }),
      );
    },
    [company, showError, actions.putCompany, showSuccess, refreshAll],
  );

  const onEditDirector = useCallback(
    (index: number, director: UserType) => {
      const users = company?.users || [];
      const formattedUsers = users.map((user, userIndex) => {
        if (userIndex !== index) return user;
        return director;
      });

      const id = company?.id;

      dispatch(
        actions.putCompany({
          body: {
            ...company,
            users: formattedUsers,
          } as Company,
          params: { id: id as string },
          onSuccess: () => {
            showSuccess('Director updated');
            setEditingIndex(null);
            refreshAll();
          },
          onError: () => {
            showError('Error director');
          },
        }),
      );
    },
    [company],
  );

  const generateUrl = (director: FormattedDirector): string => {
    const domain = `dashboard${
      APP_ENV === 'qa' ? '-qa' : ''
    }.ideal4finance.co.uk`;
    return `https://${domain}/identify/${company?.cid}?email=${director?.email}`;
  };

  return {
    onClick,
    isSubmitting,
    generateUrl,
    editingIndex,
    setEditingIndex,
    onEditDirector,
  };
}
